import React from "react";
import { Select, Input, Button, Checkbox, Tag, Divider } from "antd";
import {
  FilterOutlined,
  CloseOutlined,
  AppstoreOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

const { Option } = Select;

const LABEL_MAP = {
  productSize: "Size",
  color: "Color",
  offer: "Offer",
  fashion: "Fashion",
  productName: "Product",
  brandName: "Brand",
  groupName: "Group",
  city: "City",
  agentName: "Agent",
  entryNo: "Entry No",
  invNo: "Inv No",
  branchName: "Branch",
  hsnCode: "HSN",
  gst: "GST",
  fromPrice: "From Price",
  toPrice: "To Price",
};

const FIELD_ACCENTS = {
  productSize: { emoji: "📏", color: "#8b5cf6" },
  color: { emoji: "🎨", color: "#ec4899" },
  offer: { emoji: "🏷️", color: "#f59e0b" },
  fashion: { emoji: "👗", color: "#a855f7" },
  productName: { emoji: "🛍️", color: "#22c55e" },
  brandName: { emoji: "🔰", color: "#84cc16" },
  groupName: { emoji: "🗂️", color: "#14b8a6" },
  city: { emoji: "📍", color: "#f43f5e" },
  agentName: { emoji: "🤝", color: "#0ea5e9" },
  entryNo: { emoji: "🧾", color: "#7c3aed" },
  invNo: { emoji: "📄", color: "#f97316" },
  branchName: { emoji: "🏬", color: "#ef4444" },
  hsnCode: { emoji: "🔢", color: "#10b981" },
  gst: { emoji: "💹", color: "#eab308" },
  fromPrice: { emoji: "💰", color: "#d946ef" },
  toPrice: { emoji: "💰", color: "#d946ef" },
};

const SELECT_FIELDS = [
  ["productSize", "Product Size", "Select size...", "productSizes"],
  ["color", "Color", "Select color...", "colors"],
  ["offer", "Offer", "Select offer...", "offers"],
  ["fashion", "Fashion", "Select fashion...", "fashions"],
  ["productName", "Product Name", "Select product...", "productNames"],
  ["brandName", "Brand Name", "Select brand...", "brandNames"],
  ["groupName", "Group Name", "Select group...", "groupNames"],
  ["city", "City", "Select city...", "cities"],
  ["agentName", "Agent Name", "Select agent...", "agentNames"],
  ["entryNo", "Entry No", "Select entry no...", "entryNos"],
  ["invNo", "Inv No", "Select inv no...", "invNos"],
  ["branchName", "Branch Name", "Select branch...", "branchNames"],
  ["hsnCode", "HSN Code", "Select HSN...", "hsnCodes"],
  ["gst", "GST", "Select GST...", "gsts"],
];

const StockFilterPopoverContent = ({
  tempFilters,
  setTempFilters,
  uniqueOptions = {},
  onApply,
  onClear,
  tempSelectCheckBox,
  setTempSelectCheckBox,
  columnOptions = [],
}) => {
  const handleChange = (key, value) =>
    setTempFilters((prev) => ({ ...prev, [key]: value ?? null }));

  const isPriceKey = (key) => key === "fromPrice" || key === "toPrice";

  const removeChip = (key) =>
    setTempFilters((prev) => ({ ...prev, [key]: isPriceKey(key) ? 0 : null }));

  const activeChips = Object.entries(tempFilters).filter(([key, value]) => {
    if (isPriceKey(key)) return Number(value) > 0;
    return value !== null && value !== "" && value !== undefined;
  });
  const activeCount = activeChips.length;

  const renderField = (key, label, content) => {
    const accent = FIELD_ACCENTS[key] || { emoji: "🔧", color: "#7c3aed" };
    return (
      <div
        key={key}
        className="filterFieldCard"
        style={{
          "--field-color": accent.color,
          "--field-color-soft": `${accent.color}1a`,
          "--field-color-border": `${accent.color}55`,
        }}
      >
        <span className="filterFieldIconBox">{accent.emoji}</span>
        <div className="filterFieldBody">
          <div className="filterFieldLabel">{label}</div>
          {content}
        </div>
      </div>
    );
  };

  const renderPriceInput = (key, label) =>
    renderField(
      key,
      label,
      <Input
        placeholder={label}
        size="middle"
        inputMode="numeric"
        allowClear
        value={Number(tempFilters[key]) > 0 ? tempFilters[key] : ""}
        onChange={(e) => {
          const v = e.target.value;
          if (/^\d*$/.test(v)) handleChange(key, v === "" ? 0 : v);
        }}
        onFocus={(e) => e.target.select()}
      />,
    );

  return (
    <div className="sfp-container">
      {/* HEADER */}
      <div className="sfp-header">
        <div style={S.headerLeft}>
          <div style={S.headerIconWrap}>
            <FilterOutlined style={{ color: "#fff", fontSize: 12 }} />
          </div>
          <span style={S.headerTitle}>Smart Filters</span>
        </div>

        <div
          className={`filterTotalBlock${activeCount === 0 ? " filterTotalBlockEmpty" : ""}`}
        >
          <span className="filterTotalCountCircle">{activeCount}</span>
          <span className="filterTotalLabel">
            {activeCount > 0
              ? `Filter${activeCount > 1 ? "s" : ""} Active`
              : "No filters"}
          </span>
          {activeCount > 0 && (
            <span className="filterTotalReset" onClick={onClear}>
              <ReloadOutlined style={{ fontSize: 10 }} />
            </span>
          )}
        </div>
      </div>

      {/* ACTIVE CHIPS */}
      {activeCount > 0 && (
        <div className="sfp-tags">
          {activeChips.map(([key, value]) => (
            <Tag
              key={key}
              style={S.tag}
              closeIcon={
                <CloseOutlined style={{ fontSize: 9, color: "#7c3aed" }} />
              }
              closable
              onClose={() => removeChip(key)}
            >
              <span style={S.tagLabel}>{LABEL_MAP[key] ?? key}</span>
              <span style={S.tagDot}>·</span>
              <span style={S.tagValue}>{String(value)}</span>
            </Tag>
          ))}
        </div>
      )}

      {/* SCROLLABLE BODY */}
      <div className="sfp-body">
        <div className="sfp-fieldsGrid">
          {SELECT_FIELDS.map(([key, label, placeholder, optKey]) =>
            renderField(
              key,
              label,
              <Select
                placeholder={placeholder}
                allowClear
                showSearch
                optionFilterProp="children"
                value={tempFilters[key] ?? undefined}
                onChange={(v) => handleChange(key, v)}
                style={{ width: "100%" }}
                size="middle"
                listHeight={220}
                popupClassName="sfp-dropdown"
              >
                {(uniqueOptions[optKey] || []).map((v) => (
                  <Option key={v} value={v}>
                    {v}
                  </Option>
                ))}
              </Select>,
            ),
          )}

          {renderPriceInput("fromPrice", "From Price")}
          {renderPriceInput("toPrice", "To Price")}
        </div>

        {/* Column visibility */}
        {columnOptions.length > 0 && (
          <>
            <Divider style={S.divider} />
            <div>
              <div style={S.sectionHeader}>
                <span style={{ ...S.sectionDot, background: "#0f766e" }} />
                <span style={S.sectionLabel}>
                  <AppstoreOutlined style={{ marginRight: 4, fontSize: 10 }} />
                  Visible Columns
                </span>
                {tempSelectCheckBox && (
                  <span style={S.columnCount}>
                    {Object.values(tempSelectCheckBox).filter(Boolean).length}/
                    {columnOptions.length}
                  </span>
                )}
              </div>
              <div className="sfp-checkboxGrid">
                {columnOptions.map((item) => (
                  <label
                    key={item.value}
                    style={{
                      ...S.checkboxItem,
                      ...(item.disabled ? S.checkboxDisabled : {}),
                      ...(tempSelectCheckBox?.[item.value] && !item.disabled
                        ? S.checkboxChecked
                        : {}),
                    }}
                  >
                    <Checkbox
                      disabled={item.disabled}
                      checked={tempSelectCheckBox?.[item.value]}
                      onChange={(e) =>
                        setTempSelectCheckBox?.((prev) => ({
                          ...prev,
                          [item.value]: e.target.checked,
                        }))
                      }
                      style={{ marginRight: 5 }}
                    />
                    <span style={S.checkboxLabel}>{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* FOOTER */}
      <div className="sfp-footer">
        <Button
          onClick={onClear}
          size="small"
          style={S.resetBtn}
          icon={<ReloadOutlined />}
        >
          Reset
        </Button>
        <Button
          type="primary"
          onClick={onApply}
          size="small"
          style={S.applyBtn}
          icon={<CheckCircleOutlined />}
        >
          Apply Filters
          {activeCount > 0 && <span style={S.applyBadge}>{activeCount}</span>}
        </Button>
      </div>

      <style>{`
        /* ───────── Container (never wider than the screen) ───────── */
        .sfp-container {
          width: 780px;
          max-width: calc(100vw - 24px);
          max-height: min(560px, calc(100vh - 40px));
          max-height: min(560px, calc(100dvh - 40px));
          font-family: 'Outfit', sans-serif;
          font-size: 12px;
          display: flex;
          flex-direction: column;
          border-radius: 8px;
          box-sizing: border-box;
          overflow: hidden;
        }
        .sfp-container *, .sfp-container *::before, .sfp-container *::after {
          box-sizing: border-box;
        }

        .sfp-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
          padding: 10px 14px 8px;
          background: linear-gradient(135deg, #134e4a 0%, #0f766e 100%);
          border-radius: 8px 8px 0 0;
          flex-shrink: 0;
        }
        .sfp-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          padding: 8px 14px 6px;
          background: #f5f3ff;
          border-bottom: 1px solid #ede9fe;
          flex-shrink: 0;
          max-height: 84px;
          overflow-y: auto;
        }
        .sfp-body {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 10px 14px 4px;
          scrollbar-width: thin;
          scrollbar-color: #c4c4c4 #f1f1f1;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }

        /* minmax(0, 1fr) lets columns shrink so nothing gets clipped */
        .sfp-fieldsGrid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0 12px;
        }
        .sfp-checkboxGrid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 4px 6px;
          padding-bottom: 8px;
        }

        .sfp-footer {
          display: flex;
          gap: 8px;
          padding: 10px 14px;
          padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
          border-top: 1px solid #f0f0f0;
          background: #fafafa;
          border-radius: 0 0 8px 8px;
          flex-shrink: 0;
        }

        /* ───────── Tablet (≤ 900px): 2 columns ───────── */
        @media (max-width: 900px) {
          .sfp-container { width: 640px; }
          .sfp-fieldsGrid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .sfp-checkboxGrid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }

        /* ───────── Mobile (≤ 600px): 1 column ───────── */
        @media (max-width: 600px) {
          .sfp-container {
            max-width: calc(100vw - 16px);
            max-height: calc(100vh - 24px);
            max-height: calc(100dvh - 24px);
          }
          .sfp-header { padding: 10px 12px 8px; }
          .sfp-tags, .sfp-footer { padding-left: 12px; padding-right: 12px; }
          .sfp-body { padding: 10px 12px 4px; }
          .sfp-fieldsGrid { grid-template-columns: minmax(0, 1fr); }
          .sfp-checkboxGrid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .filterFieldCard { padding: 8px !important; margin-bottom: 8px !important; }
          .filterTotalLabel { font-size: 10px !important; }
        }

        /* Very small phones */
        @media (max-width: 360px) {
          .sfp-checkboxGrid { grid-template-columns: minmax(0, 1fr); }
          .filterFieldIconBox { display: none !important; }
        }

        /* Short landscape phones */
        @media (max-height: 480px) {
          .sfp-container {
            max-height: calc(100vh - 16px);
            max-height: calc(100dvh - 16px);
          }
        }

        /* Dropdown list must also fit narrow screens */
        .sfp-dropdown { max-width: calc(100vw - 24px); }

        /* ───────── Header pill ───────── */
        @keyframes filterTotalPulseRing {
          0%   { box-shadow: 0 0 0 0 rgba(124,58,237,0.38); }
          70%  { box-shadow: 0 0 0 8px rgba(124,58,237,0); }
          100% { box-shadow: 0 0 0 0 rgba(124,58,237,0); }
        }
        .filterTotalBlock {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px 4px 4px;
          border-radius: 999px;
          background: linear-gradient(135deg, #7c3aed, #d946ef);
          animation: filterTotalPulseRing 2.2s infinite;
          flex-shrink: 0;
        }
        .filterTotalBlockEmpty {
          background: rgba(255,255,255,0.14);
          animation: none;
        }
        .filterTotalCountCircle {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #fff;
          color: #7c3aed;
          font-weight: 800;
          font-size: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .filterTotalBlockEmpty .filterTotalCountCircle {
          background: rgba(255,255,255,0.85);
          color: #6d6d9c;
        }
        .filterTotalLabel {
          color: #fff;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.2px;
          white-space: nowrap;
        }
        .filterTotalReset {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: rgba(255,255,255,0.22);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          cursor: pointer;
          margin-left: 2px;
          transition: background 0.15s ease;
        }
        .filterTotalReset:hover { background: rgba(255,255,255,0.38); }

        /* ───────── Field cards ───────── */
        .filterFieldCard {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 8px 10px;
          margin-bottom: 10px;
          min-width: 0;
          border-radius: 10px;
          background: var(--field-color-soft, #f5f7fb);
          border: 1.4px solid var(--field-color-border, #e5e8f0);
          transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
        }
        /* hover lift only on devices that really hover (not touch) */
        @media (hover: hover) {
          .filterFieldCard:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 14px rgba(15,36,96,0.10);
            border-color: var(--field-color, #7c3aed);
          }
        }
        .filterFieldIconBox {
          width: 28px;
          height: 28px;
          border-radius: 8px;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          flex-shrink: 0;
          box-shadow: 0 1px 3px rgba(15,36,96,0.18);
          margin-top: 2px;
        }
        .filterFieldBody { flex: 1; min-width: 0; }
        .filterFieldLabel {
          font-size: 9.5px;
          font-weight: 700;
          color: var(--field-color, #666);
          margin-bottom: 5px;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .filterFieldBody .ant-select,
        .filterFieldBody .ant-input-affix-wrapper,
        .filterFieldBody .ant-input { width: 100%; max-width: 100%; }
        .filterFieldBody .ant-select-selector,
        .filterFieldBody .ant-input,
        .filterFieldBody .ant-input-affix-wrapper {
          border-radius: 7px !important;
          min-height: 38px !important;
        }
        .filterFieldBody .ant-select-selector {
          display: flex !important;
          align-items: center !important;
        }
        .filterFieldBody .ant-select-selection-search-input { height: 36px !important; }
        .filterFieldBody .ant-select-selection-item,
        .filterFieldBody .ant-select-selection-placeholder { line-height: 36px !important; }

        /* 16px inputs on touch screens stop iOS Safari from zooming on focus */
        @media (hover: none) and (pointer: coarse) {
          .filterFieldBody .ant-select-selection-search-input,
          .filterFieldBody .ant-select-selection-item,
          .filterFieldBody .ant-select-selection-placeholder,
          .filterFieldBody .ant-input { font-size: 16px !important; }
          .sfp-checkboxGrid label { padding-top: 7px !important; padding-bottom: 7px !important; }
        }
      `}</style>
    </div>
  );
};

export default StockFilterPopoverContent;

/* ─────────────── Styles (non-layout only; layout lives in the <style> block) ─────────────── */
const S = {
  headerLeft: { display: "flex", alignItems: "center", gap: 7, minWidth: 0 },
  headerIconWrap: {
    width: 22,
    height: 22,
    borderRadius: 6,
    background: "rgba(255,255,255,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: 12,
    color: "#fff",
    letterSpacing: "0.3px",
    whiteSpace: "nowrap",
  },
  tag: {
    borderRadius: 20,
    fontSize: 10,
    padding: "2px 8px",
    margin: 0,
    background: "#fff",
    border: "1px solid #ddd6fe",
    color: "#1a1a2e",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    maxWidth: "100%",
    boxShadow: "0 1px 3px rgba(124,58,237,0.12)",
  },
  tagLabel: { color: "#888", fontWeight: 500, fontSize: 10 },
  tagDot: { color: "#ccc", fontSize: 12, lineHeight: 1 },
  tagValue: {
    color: "#7c3aed",
    fontWeight: 700,
    fontSize: 10,
    maxWidth: 90,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  divider: { margin: "4px 0", borderColor: "#f0f0f0" },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 8,
  },
  sectionDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#0f766e",
    flexShrink: 0,
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#999",
    textTransform: "uppercase",
    letterSpacing: "0.7px",
    flex: 1,
  },
  columnCount: {
    fontSize: 10,
    color: "#52bd91",
    fontWeight: 700,
    background: "#f0faf5",
    padding: "1px 6px",
    borderRadius: 10,
    border: "1px solid #b7ebd4",
  },
  checkboxItem: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    padding: "3px 6px",
    borderRadius: 5,
    border: "1px solid #f0f0f0",
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s",
    background: "#fff",
    userSelect: "none",
  },
  checkboxChecked: { background: "#f5f3ff", borderColor: "#ddd6fe" },
  checkboxDisabled: { opacity: 0.5, cursor: "not-allowed" },
  checkboxLabel: { fontSize: 11, color: "#444", fontWeight: 500, minWidth: 0 },
  resetBtn: {
    flex: 1,
    borderColor: "#d9d9d9",
    color: "#666",
    fontWeight: 600,
    fontSize: 11,
    borderRadius: 6,
    height: 32,
  },
  applyBtn: {
    flex: 2,
    background: "linear-gradient(135deg, #134e4a, #0f766e)",
    border: "none",
    fontWeight: 700,
    fontSize: 11,
    borderRadius: 6,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    letterSpacing: "0.3px",
  },
  applyBadge: {
    background: "#52bd91",
    color: "#fff",
    borderRadius: 10,
    fontSize: 9,
    padding: "0 5px",
    fontWeight: 700,
    lineHeight: "14px",
    height: 14,
    display: "inline-flex",
    alignItems: "center",
  },
};
